import { Injectable } from '@angular/core';
import { injectUser } from '@quipex/core/auth';
import {
    injectDeleteDraftBuildingDialog,
    injectPublishBuildingDialog,
} from '@quipex/feature/building-context';
import {
    BuildingStatus,
    PermissionKey,
    type BuildingStatus as BuildingStatusValue,
} from '@quipex/shared/contracts';

interface FocusedBuildingActionTarget {
    readonly id: string;
    readonly name: string;
    readonly status: BuildingStatusValue;
}

@Injectable()
export class ActionState {
    //#region Dependency injection
    private readonly me = injectUser();
    private readonly publishBuildingDialog = injectPublishBuildingDialog();
    private readonly deleteDraftBuildingDialog =
        injectDeleteDraftBuildingDialog();
    //#endregion

    //#region User state
    private readonly hasPublishPermission = this.me.hasPermission([
        PermissionKey.OrgBuildingPublish,
    ]);
    private readonly hasDeletePermission = this.me.hasPermission([
        PermissionKey.OrgBuildingCreate,
    ]);
    //#endregion

    //#region Public methods
    public canPublish(target: FocusedBuildingActionTarget): boolean {
        return (
            this.hasPublishPermission() &&
            target.status === BuildingStatus.Draft
        );
    }

    public canDelete(target: FocusedBuildingActionTarget): boolean {
        return (
            this.hasDeletePermission() && target.status === BuildingStatus.Draft
        );
    }

    public publish(target: FocusedBuildingActionTarget): void {
        if (!this.canPublish(target)) return;

        this.publishBuildingDialog.open({ buildingId: target.id });
    }

    public delete(
        target: FocusedBuildingActionTarget,
        onDeleted: () => void
    ): void {
        if (!this.canDelete(target)) return;

        this.deleteDraftBuildingDialog.open({
            buildingId: target.id,
            buildingName: target.name,
            onDeleted,
        });
    }
    //#endregion
}
